import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Modal,
    Box,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    Tooltip,
} from '@mui/material';

import {
    Loading,
    ErrorMessage,
    StatusMessage,
    SimpleSingleSelect,
    SimpleCheckbox,
    TrashIcon,
    DataTable,
    type TableColumn,
} from '@/src/ui';
import {
    Constants,
    type Option,
    getZeroOption,
    ReportHeaderStyle,
    ReportCellStyle,
} from '@/src/utils';
import { useAuth } from '@/src/app/context/useAuth';
import Roles from '@/src/app/Roles';
import { apiRequest } from '@/src/services';
import type {
    QuizListItemDTO,
    QuizPermissionCreateDTO,
    QuizPermissionDTO,
    ShareTargetType,
} from '../models/quizDTOs';
import type { DistrictBaseDTO } from '@/src/pages/rostering/districts/models/districtDTOs';
import type { SchoolBaseDTO } from '@/src/pages/rostering/schools/models/schoolDTOs';
import type { UserBasicDTO } from '@/src/pages/rostering/users/models/userDTOs';

interface ShareQuizModalProps {
    open: boolean;
    quiz: QuizListItemDTO;
    onClose: () => void;
}

const getAvailableTargetTypes = (role?: string): ShareTargetType[] => {
    switch (role) {
        case Roles.SuperAdmin:
        case Roles.Coach:
            return ['district', 'school', 'user'];
        case Roles.DistrictAdmin:
            return ['district', 'school', 'user'];
        case Roles.SchoolAdmin:
            return ['school', 'user'];
        case Roles.Teacher:
            return ['user'];
        default:
            return [];
    }
};

const describePermission = (p: QuizPermissionDTO): string => {
    if (p.sharedWith_UserId) return `User: ${p.sharedWith_UserName ?? `#${p.sharedWith_UserId}`}`;
    if (p.sharedWith_SchoolId)
        return `School: ${p.sharedWith_SchoolName ?? `#${p.sharedWith_SchoolId}`}`;
    if (p.sharedWith_DistrictId)
        return `District: ${p.sharedWith_DistrictName ?? `#${p.sharedWith_DistrictId}`}`;
    return 'Unknown recipient';
};

const ShareQuizModal: React.FC<ShareQuizModalProps> = ({ open, quiz, onClose }) => {
    const { user } = useAuth();
    const availableTargetTypes = useMemo(() => getAvailableTargetTypes(user?.role), [user?.role]);

    const [targetType, setTargetType] = useState<ShareTargetType>(
        availableTargetTypes[0] ?? 'user'
    );
    const [targetId, setTargetId] = useState<number>(0);
    const [isEditingAccessGranted, setIsEditingAccessGranted] = useState(false);

    const [targetOptions, setTargetOptions] = useState<Option[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(false);

    const [permissions, setPermissions] = useState<QuizPermissionDTO[]>([]);
    const [permissionsLoading, setPermissionsLoading] = useState(false);
    const [revokingId, setRevokingId] = useState<number | null>(null);

    const [submitting, setSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [error, setError] = useState('');

    const refreshPermissions = useCallback(async () => {
        setPermissionsLoading(true);
        try {
            const res = await apiRequest<QuizPermissionDTO[]>(
                'GET',
                `assessments/exams/quizzes/${quiz.id}/permissions`
            );
            setPermissions(res.data ?? []);
        } catch {
            setError('Failed to load existing shares');
        } finally {
            setPermissionsLoading(false);
        }
    }, [quiz.id]);

    useEffect(() => {
        refreshPermissions();
    }, [refreshPermissions]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setOptionsLoading(true);
            setError('');
            setTargetId(0);
            try {
                let options: Option[] = [];
                if (targetType === 'district') {
                    const res = await apiRequest<DistrictBaseDTO[]>('GET', 'dbo/districts');
                    options = (res.data ?? [])
                        .filter((d) => d.id != null)
                        .map((d) => ({ id: d.id as number, name: d.name }));
                } else if (targetType === 'school') {
                    const res = await apiRequest<SchoolBaseDTO[]>('GET', 'dbo/schools');
                    options = (res.data ?? [])
                        .filter((s) => s.id != null)
                        .map((s) => ({ id: s.id as number, name: s.name }));
                } else {
                    const utm = Constants.USER_TYPE_MASTER_IDS;
                    const userResults = await Promise.all(
                        [utm.TEACHER, utm.SCHOOL_ADMIN, utm.DISTRICT_ADMIN].map((id) =>
                            apiRequest<UserBasicDTO[]>('GET', `dbo/users?userTypeMasterId=${id}`)
                        )
                    );
                    const merged = userResults.flatMap((res) => res.data ?? []);
                    options = merged.map((u) => ({
                        id: u.id,
                        name: `${u.fName} ${u.lName} (${u.username})`,
                    }));
                }
                if (cancelled) return;
                options.sort((a, b) => a.name.localeCompare(b.name));
                if (!options.length) {
                    options = [getZeroOption(`No ${targetType}s available`)];
                } else {
                    options.unshift(getZeroOption(`Select a ${targetType}`));
                }
                setTargetOptions(options);
            } catch {
                if (cancelled) return;
                setError('Failed to load recipients');
                setTargetOptions([getZeroOption('Failed to load recipients')]);
            } finally {
                if (!cancelled) setOptionsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [targetType]);

    const handleAttemptClose = () => {
        if (submitting || revokingId !== null) return;
        onClose();
    };

    const handleShare = async () => {
        if (submitting) return;
        if (!targetId) {
            setError('Please select a recipient');
            return;
        }
        setSubmitting(true);
        setError('');
        setStatusMessage('');
        try {
            const payload: QuizPermissionCreateDTO = {
                isEditingAccessGranted,
                ...(targetType === 'district' && { sharedWith_DistrictId: targetId }),
                ...(targetType === 'school' && { sharedWith_SchoolId: targetId }),
                ...(targetType === 'user' && { sharedWith_UserId: targetId }),
            };
            await apiRequest('POST', `assessments/exams/quizzes/${quiz.id}/permissions`, payload);
            setStatusMessage('Quiz shared successfully!');
            setTargetId(0);
            setIsEditingAccessGranted(false);
            await refreshPermissions();
        } catch {
            setError('Failed to share quiz');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRevoke = useCallback(
        async (permission: QuizPermissionDTO) => {
            if (revokingId !== null) return;
            const confirmed = window.confirm(
                `Revoke share with ${describePermission(permission)}?`
            );
            if (!confirmed) return;
            setRevokingId(permission.id);
            setError('');
            setStatusMessage('');
            try {
                await apiRequest(
                    'DELETE',
                    `assessments/exams/quizzes/${quiz.id}/permissions/${permission.id}`
                );
                setStatusMessage('Share revoked');
                await refreshPermissions();
            } catch {
                setError('Failed to revoke share');
            } finally {
                setRevokingId(null);
            }
        },
        [revokingId, quiz.id, refreshPermissions]
    );

    const targetTypeLabels: Record<ShareTargetType, string> = {
        district: 'District',
        school: 'School',
        user: 'User',
    };

    const permissionColumns = useMemo<TableColumn[]>(
        () => [
            {
                dataKey: 'recipient',
                header: 'Recipient',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: QuizPermissionDTO) => describePermission(row),
            },
            {
                dataKey: 'access',
                header: 'Access',
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: QuizPermissionDTO) =>
                    row.isEditingAccessGranted ? 'Can edit' : 'View only',
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: QuizPermissionDTO) => (
                    <Tooltip title="Revoke" arrow>
                        <span>
                            <button
                                disabled={revokingId !== null}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: revokingId !== null ? 'not-allowed' : 'pointer',
                                    opacity: revokingId === row.id ? 0.5 : 1,
                                }}
                                onClick={() => handleRevoke(row)}
                            >
                                <span role="img" aria-label="revoke">
                                    <TrashIcon />
                                </span>
                            </button>
                        </span>
                    </Tooltip>
                ),
            },
        ],
        [revokingId, handleRevoke]
    );

    return (
        <Modal open={open} onClose={handleAttemptClose}>
            <Box
                sx={{
                    p: 4,
                    bgcolor: 'background.paper',
                    margin: 'auto',
                    mt: 8,
                    width: 800,
                    maxWidth: '95vw',
                    maxHeight: '85vh',
                    overflowY: 'auto',
                }}
            >
                <h3 style={{ marginTop: 0 }}>Share &quot;{quiz.title}&quot;</h3>
                <br />

                {permissionsLoading ? (
                    <Loading visible message="Loading shares..." size="sm" />
                ) : permissions.length === 0 ? (
                    <p style={{ color: '#666', fontStyle: 'italic', margin: '4px 0 12px' }}>
                        Not currently shared
                    </p>
                ) : (
                    <Box sx={{ mb: 2 }}>
                        <DataTable
                            data={permissions}
                            columns={permissionColumns}
                            tableName="QuizPermissions"
                            alternatingRowColor
                            defaultSortColumn="recipient"
                            defaultSortDirection="asc"
                            rowsPerPageList={[50]}
                            rowsPerPageInitial={50}
                        />
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <h4 style={{ marginBottom: 8 }}>Share this quiz</h4>
                {availableTargetTypes.length === 0 ? (
                    <p>You don&apos;t have permission to share quizzes.</p>
                ) : (
                    <>
                        <RadioGroup
                            row
                            value={targetType}
                            onChange={(e) => setTargetType(e.target.value as ShareTargetType)}
                            sx={{ mb: 2 }}
                        >
                            {availableTargetTypes.map((t) => (
                                <FormControlLabel
                                    key={t}
                                    value={t}
                                    control={<Radio />}
                                    label={targetTypeLabels[t]}
                                />
                            ))}
                        </RadioGroup>

                        <SimpleSingleSelect
                            name="shareTarget"
                            label={`Choose ${targetTypeLabels[targetType]}`}
                            value={targetId}
                            onChange={(e) => setTargetId(Number(e.target.value))}
                            options={targetOptions}
                            disabled={optionsLoading}
                            sx={{ width: '100%', mb: 2 }}
                        />

                        <SimpleCheckbox
                            name="isEditingAccessGranted"
                            label={`Allow ${targetType} to edit`}
                            checked={isEditingAccessGranted}
                            onChange={(e) => setIsEditingAccessGranted(e.target.checked)}
                        />

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 2,
                            }}
                        >
                            <Button
                                disabled={submitting || revokingId !== null}
                                color="error"
                                variant="outlined"
                                onClick={onClose}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={submitting || optionsLoading || !targetId}
                                variant="contained"
                                onClick={handleShare}
                            >
                                Share
                            </Button>
                        </Box>
                    </>
                )}

                {availableTargetTypes.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                        <Button
                            disabled={submitting || revokingId !== null}
                            color="error"
                            variant="outlined"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </Box>
                )}

                <ErrorMessage error={error} />
                <StatusMessage
                    visible={!!statusMessage}
                    status={statusMessage}
                    style={{ marginTop: 8 }}
                />
                <Loading visible={submitting} message="Sharing..." size="sm" />
                <Loading visible={revokingId !== null} message="Revoking..." size="sm" />
            </Box>
        </Modal>
    );
};

export default ShareQuizModal;
